
import React from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Steps, Step } from "@/components/ui/steps";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Info, Wifi, ArrowRight, Smartphone, Check, AlertTriangle, RefreshCw, Search, WifiOff } from "lucide-react";

const ESPWiFiSetupGuide: React.FC = () => {
  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>ESP32 WiFi Setup Guide</CardTitle>
        <CardDescription>
          Follow these steps to connect your ESP32 device to your home WiFi network
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Alert className="mb-4 bg-yellow-50 border-yellow-200">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertTitle>🔴 IMPORTANT: ESP32 Network May Be Hidden</AlertTitle>
          <AlertDescription>
            <p className="font-medium text-red-600 mb-2">
              The ESP32 WiFi network may appear as a "Hidden Network." You will likely need to manually 
              connect to it using the instructions below in Step 3.
            </p>
            <ul className="list-disc pl-5 mt-1 space-y-1">
              <li>Make sure your ESP32 is powered on and the blue LED is blinking</li>
              <li>Keep your phone or computer within 3-5 feet of the ESP32 during setup</li>
              <li>If you can't see the ESP32-Setup network, try the "Connect to Hidden Network" option</li>
              <li>Some Android devices may need Location Services enabled to see networks</li>
              <li>Try restarting the ESP32 by unplugging it, waiting 10 seconds, then plugging it back in</li>
              <li>The ESP32 broadcasts on WiFi channel 1 - make sure your device can see that channel</li>
              <li>Try different devices (phone, laptop, tablet) as some detect networks better than others</li>
            </ul>
          </AlertDescription>
        </Alert>
        
        <Steps>
          <Step icon={<Wifi className="h-5 w-5" />} title="Power On Device">
            <p className="text-sm text-muted-foreground mt-2">
              Power on your ESP32 device. A blue LED should start blinking, indicating it's in setup mode.
            </p>
          </Step>
          
          <Step icon={<Smartphone className="h-5 w-5" />} title="Check WiFi Settings">
            <p className="text-sm text-muted-foreground mt-2">
              On your phone or computer, check the available WiFi networks. Look for a network named 
              <strong> ESP32-Setup</strong>. It doesn't require a password. The network name may include 
              numbers (e.g., ESP32-Setup-XXXX).
            </p>
            <Alert className="mt-2 bg-blue-50 border-blue-200">
              <Info className="h-4 w-4 text-blue-500" />
              <AlertTitle>Troubleshooting WiFi Visibility</AlertTitle>
              <AlertDescription>
                <ul className="list-disc pl-5 mt-1 space-y-1">
                  <li>If you don't see the network, try refreshing your WiFi list multiple times</li>
                  <li>Move closer to the ESP32 device - within 3-5 feet is ideal</li>
                  <li>Some devices may need to be restarted to see new networks</li>
                  <li>Try using a different device (phone/tablet/laptop) to find the network</li>
                  <li>The ESP32 works on 2.4GHz only - make sure your device can see 2.4GHz networks</li>
                </ul>
              </AlertDescription>
            </Alert>
          </Step>
          
          <Step icon={<Search className="h-5 w-5" />} title="Connect to Hidden Network">
            <p className="text-sm text-muted-foreground mt-2 font-medium text-red-600">
              IMPORTANT: The ESP32-Setup network is often detected as a "Hidden Network" or may not show up at all.
              You will need to manually connect to it as a hidden network.
            </p>
            <Alert className="mt-2 bg-orange-50 border-orange-200">
              <Info className="h-4 w-4 text-orange-500" />
              <AlertTitle>Connecting to Hidden Networks</AlertTitle>
              <AlertDescription>
                <ul className="list-disc pl-5 mt-1 space-y-1">
                  <li><strong>On Windows:</strong> Click "Hidden Network" in your WiFi list, then enter "ESP32-Setup" as the network name</li>
                  <li><strong>On macOS:</strong> Click "Join Other Network..." from the WiFi menu, then enter "ESP32-Setup"</li>
                  <li><strong>On iPhone:</strong> Go to Settings → WiFi → Other..., then enter "ESP32-Setup"</li>
                  <li><strong>On Android:</strong> Tap "Add network" or "+" icon, then enter "ESP32-Setup" as the network name</li>
                  <li>Leave the password blank as the ESP32's WiFi network is open</li>
                  <li>If "ESP32-Setup" doesn't work, try "ESP32-Setup-XXXX" where XXXX might be numbers from the device</li>
                </ul>
              </AlertDescription>
            </Alert>
            <div className="mt-4 flex items-center justify-center">
              <WifiOff className="h-12 w-12 text-orange-500 mr-3" />
              <div className="text-lg font-medium text-orange-700">
                Follow the "Hidden Network" instructions above even if no networks show up!
              </div>
            </div>
          </Step>
          
          <Step icon={<ArrowRight className="h-5 w-5" />} title="Open Configuration Page">
            <p className="text-sm text-muted-foreground mt-2">
              A setup page should appear automatically. If not, open a web browser and navigate to <strong>http://192.168.4.1</strong>.
            </p>
            <Alert className="mt-2 bg-blue-50 border-blue-200">
              <Info className="h-4 w-4 text-blue-500" />
              <AlertTitle>Troubleshooting</AlertTitle>
              <AlertDescription>
                If the page doesn't load, try <strong>http://esp32.local</strong> or <strong>http://192.168.4.1</strong> in your browser.
                On Android, you may need to turn off mobile data first.
              </AlertDescription>
            </Alert>
          </Step>
          
          <Step icon={<Wifi className="h-5 w-5" />} title="Enter WiFi Credentials">
            <p className="text-sm text-muted-foreground mt-2">
              On the configuration page, select your home WiFi network from the list and enter the password.
              Then click "Connect".
            </p>
          </Step>
          
          <Step icon={<Check className="h-5 w-5" />} title="Complete Setup">
            <p className="text-sm text-muted-foreground mt-2">
              The ESP32 will save your credentials and connect to your WiFi network. The LED will 
              stop blinking and remain solid when connected successfully.
            </p>
            <Alert className="mt-2 bg-green-50 border-green-200">
              <Info className="h-4 w-4 text-green-500" />
              <AlertTitle>Reconnect to your home WiFi</AlertTitle>
              <AlertDescription>
                You can now reconnect your device to your regular WiFi network.
              </AlertDescription>
            </Alert>
          </Step>
        </Steps>
        
        <Alert className="mt-6 bg-red-50 border-red-200">
          <RefreshCw className="h-4 w-4 text-red-500" />
          <AlertTitle>Still Having Trouble?</AlertTitle>
          <AlertDescription>
            <p className="mb-2">If you still can't see or connect to the ESP32 WiFi network:</p>
            <ol className="list-decimal pl-5 space-y-1">
              <li>Hold the RESET button for 10 seconds, then release to restart the ESP32</li>
              <li>Try using a USB power adapter that provides stable 5V with at least 500mA</li>
              <li>Move away from other sources of WiFi interference</li>
              <li>Try connecting to the ESP32 in an area with fewer WiFi networks</li>
              <li><strong>Always try the "Connect to Hidden Network" option and enter "ESP32-Setup"</strong></li>
              <li>If all else fails, try connecting via USB and monitor the Serial output</li>
            </ol>
          </AlertDescription>
        </Alert>
      </CardContent>
      <CardFooter className="border-t pt-4">
        <p className="text-sm text-muted-foreground">
          If setup fails, hold the RESET button for 10 seconds to restart the process. For assistance, 
          contact support.
        </p>
      </CardFooter>
    </Card>
  );
};

export default ESPWiFiSetupGuide;
